import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../components/ui/dialog';
import { 
    Layers,
    Plus,
    Minus,
    Trash2,
    Edit,
    Package,
    TrendingUp,
    Search,
    RefreshCw,
    Loader2,
    Check,
    X,
    AlertTriangle,
    Upload,
    Image as ImageIcon,
    Droplet,
    Wine,
    Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image upload constants
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const CompositeProductsPage = () => {
    const [compositeProducts, setCompositeProducts] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    // Image upload state
    const [uploading, setUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    
    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        ingredients: [],
        ingredient_groups: [], // Novo: grupos de ingredientes com opções
        image_url: ''
    });
    
    // State para gerenciar grupos de ingredientes
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupUseDose, setNewGroupUseDose] = useState(true);
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(null);
    
    // Search
    const [searchQuery, setSearchQuery] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [groupProductSearch, setGroupProductSearch] = useState(''); // Busca separada para grupos

    const fetchCompositeProducts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/composite-products?active_only=false`);
            setCompositeProducts(response.data);
        } catch (error) {
            console.error('Error fetching composite products:', error);
            toast.error('Erro ao carregar produtos compostos');
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/products`);
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchCompositeProducts(), fetchProducts()]);
            setLoading(false);
        };
        loadData();
    }, [fetchCompositeProducts, fetchProducts]);

    // Image upload functions
    const uploadToCloudinary = async (file) => {
        try {
            const signatureResponse = await axios.get(`${API_URL}/api/cloudinary/signature?folder=composite-products`);
            const { signature, timestamp, cloud_name, api_key, folder } = signatureResponse.data;

            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('api_key', api_key);
            uploadFormData.append('timestamp', timestamp);
            uploadFormData.append('signature', signature);
            uploadFormData.append('folder', folder);

            const uploadResponse = await fetch(
                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                { method: 'POST', body: uploadFormData }
            );

            const result = await uploadResponse.json();
            
            if (result.error) {
                throw new Error(result.error.message);
            }

            return result.secure_url;
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            throw error;
        }
    };

    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            toast.error('Formato não suportado. Use JPG, PNG ou WebP');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            toast.error('Imagem muito grande. Máximo 2MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

        setUploading(true);
        try {
            const imageUrl = await uploadToCloudinary(file);
            setFormData(prev => ({ ...prev, image_url: imageUrl }));
            toast.success('Imagem enviada com sucesso!');
        } catch (error) {
            toast.error('Erro ao enviar imagem');
            setImagePreview(formData.image_url || null);
        } finally {
            setUploading(false);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, image_url: '' }));
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            price: '',
            ingredients: [],
            ingredient_groups: [],
            image_url: ''
        });
        setProductSearch('');
        setGroupProductSearch(''); // Reset busca de grupos também
        setImagePreview(null);
        setSelectedGroupIndex(null);
        setNewGroupName('');
        setNewGroupUseDose(true);
    };

    const addIngredient = (product) => {
        const existingIndex = formData.ingredients.findIndex(ing => ing.product_id === product.id);
        if (existingIndex >= 0) {
            // Increment quantity
            const newIngredients = [...formData.ingredients];
            newIngredients[existingIndex].quantity += 1;
            setFormData({ ...formData, ingredients: newIngredients });
        } else {
            // Add new ingredient
            setFormData({
                ...formData,
                ingredients: [...formData.ingredients, {
                    product_id: product.id,
                    product_name: product.name,
                    quantity: 1,
                    use_dose: false, // Default: usa item inteiro
                    cost_price: product.cost_price,
                    price: product.price,
                    sold_by_dose: product.sold_by_dose || false,
                    doses_per_bottle: product.doses_per_bottle || 1
                }]
            });
        }
        setProductSearch('');
    };

    const updateIngredientQuantity = (index, delta) => {
        const newIngredients = [...formData.ingredients];
        newIngredients[index].quantity += delta;
        if (newIngredients[index].quantity <= 0) {
            newIngredients.splice(index, 1);
        }
        setFormData({ ...formData, ingredients: newIngredients });
    };

    const toggleUseDose = (index) => {
        const newIngredients = [...formData.ingredients];
        newIngredients[index].use_dose = !newIngredients[index].use_dose;
        setFormData({ ...formData, ingredients: newIngredients });
    };

    const removeIngredient = (index) => {
        const newIngredients = [...formData.ingredients];
        newIngredients.splice(index, 1);
        setFormData({ ...formData, ingredients: newIngredients });
    };

    // ========== FUNÇÕES PARA GRUPOS DE INGREDIENTES (OPÇÕES) ==========
    
    // Adicionar novo grupo de ingredientes
    const addIngredientGroup = () => {
        if (!newGroupName.trim()) {
            toast.error('Informe o nome do grupo');
            return;
        }
        
        const newGroup = {
            group_name: newGroupName.trim(),
            quantity: 1,
            use_dose: newGroupUseDose,
            required: true,
            options: []
        };
        
        setFormData({
            ...formData,
            ingredient_groups: [...formData.ingredient_groups, newGroup]
        });
        setNewGroupName('');
        setNewGroupUseDose(true);
        toast.success(`Grupo "${newGroup.group_name}" criado! Agora adicione as opções.`);
    };
    
    // Remover grupo de ingredientes
    const removeIngredientGroup = (groupIndex) => {
        const newGroups = [...formData.ingredient_groups];
        newGroups.splice(groupIndex, 1);
        setFormData({ ...formData, ingredient_groups: newGroups });
        if (selectedGroupIndex === groupIndex) {
            setSelectedGroupIndex(null);
        }
    };
    
    // Adicionar opção a um grupo
    const addOptionToGroup = (groupIndex, product) => {
        const newGroups = [...formData.ingredient_groups];
        const group = newGroups[groupIndex];
        
        // Verificar se já existe
        if (group.options.some(opt => opt.product_id === product.id)) {
            toast.error('Este produto já está nas opções');
            return;
        }
        
        // Verificar se o produto é compatível com use_dose do grupo
        if (group.use_dose && !product.sold_by_dose) {
            toast.error(`${product.name} não é vendido por dose. Escolha outro produto.`);
            return;
        }
        
        group.options.push({
            product_id: product.id,
            product_name: product.name
        });
        
        setFormData({ ...formData, ingredient_groups: newGroups });
        toast.success(`${product.name} adicionado ao grupo "${group.group_name}"`);
    };
    
    // Remover opção de um grupo
    const removeOptionFromGroup = (groupIndex, optionIndex) => {
        const newGroups = [...formData.ingredient_groups];
        newGroups[groupIndex].options.splice(optionIndex, 1);
        setFormData({ ...formData, ingredient_groups: newGroups });
    };
    
    // Atualizar quantidade do grupo
    const updateGroupQuantity = (groupIndex, delta) => {
        const newGroups = [...formData.ingredient_groups];
        const newQty = newGroups[groupIndex].quantity + delta;
        if (newQty >= 1) {
            newGroups[groupIndex].quantity = newQty;
            setFormData({ ...formData, ingredient_groups: newGroups });
        }
    };
    
    // Filtrar produtos para adicionar às opções
    const getFilteredProductsForGroup = (group) => {
        if (!group) return [];
        
        const searchLower = groupProductSearch.toLowerCase();
        return products.filter(p => {
            // Filtrar por busca
            const matchesSearch = !groupProductSearch || p.name.toLowerCase().includes(searchLower);
            
            // Se o grupo usa dose, mostrar apenas produtos vendidos por dose
            if (group.use_dose) {
                return matchesSearch && p.sold_by_dose && p.active;
            }
            
            return matchesSearch && p.active;
        });
    };

    const calculateTotalCost = () => {
        return formData.ingredients.reduce((sum, ing) => {
            if (ing.use_dose && ing.sold_by_dose) {
                // Custo por dose
                const costPerDose = ing.cost_price / (ing.doses_per_bottle || 1);
                return sum + (costPerDose * ing.quantity);
            }
            // Custo do item inteiro
            return sum + (ing.cost_price * ing.quantity);
        }, 0);
    };

    const handleCreate = async () => {
        if (!formData.name.trim()) {
            toast.error('Informe o nome do produto composto');
            return;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            toast.error('Informe o preço do produto composto');
            return;
        }
        
        // Verificar se tem ingredientes fixos OU grupos de opções
        const hasFixedIngredients = formData.ingredients.length > 0;
        const hasIngredientGroups = formData.ingredient_groups.length > 0;
        
        if (!hasFixedIngredients && !hasIngredientGroups) {
            toast.error('Adicione ingredientes fixos ou grupos de opções');
            return;
        }
        
        // Validar que todos os grupos tenham pelo menos 1 opção
        if (hasIngredientGroups) {
            const emptyGroups = formData.ingredient_groups.filter(g => g.options.length === 0);
            if (emptyGroups.length > 0) {
                toast.error(`Adicione opções ao grupo: ${emptyGroups[0].group_name}`);
                return;
            }
        }

        setProcessing(true);
        try {
            await axios.post(`${API_URL}/api/composite-products`, {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                ingredients: formData.ingredients.map(({ product_id, product_name, quantity, use_dose }) => ({
                    product_id,
                    product_name,
                    quantity,
                    use_dose
                })),
                ingredient_groups: hasIngredientGroups ? formData.ingredient_groups : null,
                image_url: formData.image_url || null
            });
            toast.success('Produto composto criado com sucesso!');
            setCreateDialogOpen(false);
            resetForm();
            fetchCompositeProducts();
        } catch (error) {
            console.error('Error creating composite product:', error);
            toast.error(error.response?.data?.detail || 'Erro ao criar produto composto');
        } finally {
            setProcessing(false);
        }
    };

    const handleEdit = async () => {
        if (!formData.name.trim() || !formData.price) {
            toast.error('Preencha nome e preço');
            return;
        }
        
        // Verificar se tem ingredientes fixos OU grupos de opções
        const hasFixedIngredients = formData.ingredients.length > 0;
        const hasIngredientGroups = formData.ingredient_groups.length > 0;
        
        if (!hasFixedIngredients && !hasIngredientGroups) {
            toast.error('Adicione ingredientes fixos ou grupos de opções');
            return;
        }

        setProcessing(true);
        try {
            await axios.put(`${API_URL}/api/composite-products/${selectedProduct.id}`, {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                ingredients: formData.ingredients.map(({ product_id, product_name, quantity, use_dose }) => ({
                    product_id,
                    product_name,
                    quantity,
                    use_dose
                })),
                ingredient_groups: hasIngredientGroups ? formData.ingredient_groups : null,
                image_url: formData.image_url || null
            });
            toast.success('Produto composto atualizado!');
            setEditDialogOpen(false);
            resetForm();
            fetchCompositeProducts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar produto composto');
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedProduct) return;

        setProcessing(true);
        try {
            await axios.delete(`${API_URL}/api/composite-products/${selectedProduct.id}`);
            toast.success('Produto composto desativado!');
            setDeleteDialogOpen(false);
            setSelectedProduct(null);
            fetchCompositeProducts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao desativar produto composto');
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleActive = async (product) => {
        try {
            await axios.put(`${API_URL}/api/composite-products/${product.id}`, {
                active: !product.active
            });
            toast.success(product.active ? 'Produto desativado' : 'Produto ativado');
            fetchCompositeProducts();
        } catch (error) {
            toast.error('Erro ao alterar status');
        }
    };

    const openEditDialog = (product) => {
        setSelectedProduct(product);
        setFormData({
            name: product.name,
            description: product.description || '',
            price: product.price.toString(),
            ingredients: product.ingredients?.map(ing => {
                const prod = products.find(p => p.id === ing.product_id);
                return {
                    ...ing,
                    cost_price: prod?.cost_price || 0,
                    price: prod?.price || 0,
                    sold_by_dose: prod?.sold_by_dose || false,
                    doses_per_bottle: prod?.doses_per_bottle || 1
                };
            }) || [],
            ingredient_groups: product.ingredient_groups || [],
            image_url: product.image_url || ''
        });
        setImagePreview(product.image_url || null);
        setSelectedGroupIndex(null);
        setEditDialogOpen(true);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const filteredCompositeProducts = compositeProducts.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) &&
        product.active
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6 animate-fade-in-up" data-testid="composite-products-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl sm:text-3xl font-heading font-bold text-white">Produtos Compostos</h1>
                    <p className="text-gray-400 text-sm mt-1 hidden sm:block">Produtos que utilizam ingredientes do estoque</p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#262626] text-gray-400"
                        onClick={fetchCompositeProducts}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                        className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold text-sm"
                        onClick={() => { resetForm(); setCreateDialogOpen(true); }}
                    >
                        <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Novo Produto Composto</span>
                        <span className="sm:hidden">Novo</span>
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                    placeholder="Buscar produtos compostos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#121212] border-[#262626] text-white"
                />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-rr-yellow-500/20 flex items-center justify-center">
                                <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-rr-yellow-500" />
                            </div>
                            <div className="text-center sm:text-left">
                                <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Total</p>
                                <p className="text-lg sm:text-2xl font-bold text-white">{compositeProducts.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-emerald-500/20 flex items-center justify-center">
                                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                            </div>
                            <div className="text-center sm:text-left">
                                <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Ativos</p>
                                <p className="text-lg sm:text-2xl font-bold text-emerald-500">{compositeProducts.filter(c => c.active).length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-purple-500/20 flex items-center justify-center">
                                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                            </div>
                            <div className="text-center sm:text-left">
                                <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Margem</p>
                                <p className="text-lg sm:text-2xl font-bold text-purple-500">
                                    {compositeProducts.length > 0 
                                        ? `${(compositeProducts.reduce((s, c) => s + (c.profit_percentage || 0), 0) / compositeProducts.length).toFixed(0)}%`
                                        : '0%'
                                    }
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Products List */}
            <Card className="bg-[#121212] border-[#262626]">
                <CardContent className="p-0">
                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-2 p-3">
                        {filteredCompositeProducts.map(product => (
                            <div 
                                key={product.id}
                                className="p-3 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className="w-10 h-10 bg-orange-500/20 rounded-md flex items-center justify-center flex-shrink-0">
                                            <Layers className="w-5 h-5 text-orange-500" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-white font-medium text-sm truncate">{product.name}</p>
                                            <Badge 
                                                className={`text-[10px] mt-1 ${product.active 
                                                    ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' 
                                                    : 'bg-red-500/20 text-red-500 border-red-500/30'
                                                }`}
                                                onClick={() => handleToggleActive(product)}
                                            >
                                                {product.active ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-gray-400 hover:text-white h-8 w-8"
                                            onClick={() => openEditDialog(product)}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-gray-400 hover:text-red-500 h-8 w-8"
                                            onClick={() => { setSelectedProduct(product); setDeleteDialogOpen(true); }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Ingredientes */}
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {product.ingredients.slice(0, 3).map((ing, idx) => (
                                        <Badge 
                                            key={idx} 
                                            variant="outline" 
                                            className={`text-[10px] ${ing.use_dose ? 'text-blue-400 border-blue-500/30' : 'text-gray-400 border-[#333]'}`}
                                        >
                                            {ing.quantity}x {ing.product_name.split(' ')[0]}
                                            {ing.use_dose && <Droplet className="w-2.5 h-2.5 ml-0.5 inline" />}
                                        </Badge>
                                    ))}
                                    {product.ingredients.length > 3 && (
                                        <Badge variant="outline" className="text-[10px] text-gray-500 border-[#333]">
                                            +{product.ingredients.length - 3}
                                        </Badge>
                                    )}
                                </div>
                                
                                {/* Valores */}
                                <div className="mt-3 pt-2 border-t border-[#1a1a1a] grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-[10px] text-gray-500">Custo</p>
                                        <p className="text-xs text-gray-400">{formatCurrency(product.total_cost)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-500">Preço</p>
                                        <p className="text-xs text-white font-medium">{formatCurrency(product.price)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-500">Lucro</p>
                                        <p className="text-xs text-emerald-500 font-medium">
                                            {formatCurrency(product.profit_margin)}
                                            <span className="text-[10px] text-gray-500 ml-1">({product.profit_percentage?.toFixed(0)}%)</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {filteredCompositeProducts.length === 0 && (
                            <div className="text-center py-12 text-gray-500">
                                <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>Nenhum produto composto cadastrado</p>
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden lg:block">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-[#262626] hover:bg-transparent">
                                    <TableHead className="text-gray-400">Produto</TableHead>
                                    <TableHead className="text-gray-400">Ingredientes</TableHead>
                                    <TableHead className="text-gray-400 text-right">Custo</TableHead>
                                    <TableHead className="text-gray-400 text-right">Preço</TableHead>
                                    <TableHead className="text-gray-400 text-right">Lucro</TableHead>
                                    <TableHead className="text-gray-400 text-center">Status</TableHead>
                                    <TableHead className="text-gray-400 w-24"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCompositeProducts.map(product => (
                                    <TableRow key={product.id} className="border-[#262626] hover:bg-white/5">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-orange-500/20 rounded-md flex items-center justify-center">
                                                    <Layers className="w-5 h-5 text-orange-500" />
                                                </div>
                                                <div>
                                                    <p className="text-white font-medium">{product.name}</p>
                                                    {product.description && (
                                                        <p className="text-xs text-gray-500 truncate max-w-[200px]">{product.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {product.ingredients.map((ing, idx) => (
                                                    <Badge 
                                                        key={idx} 
                                                        variant="outline" 
                                                        className={`border-[#333] text-xs ${ing.use_dose ? 'text-blue-400 border-blue-500/30' : 'text-gray-400'}`}
                                                    >
                                                        {ing.quantity}x {ing.product_name.split(' ').slice(0, 2).join(' ')}
                                                        {ing.use_dose && <Droplet className="w-3 h-3 ml-1 inline" />}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right text-gray-400">
                                            {formatCurrency(product.total_cost)}
                                        </TableCell>
                                        <TableCell className="text-right text-white font-medium">
                                            {formatCurrency(product.price)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div>
                                                <span className="text-emerald-500 font-medium">{formatCurrency(product.profit_margin)}</span>
                                                <span className="text-xs text-gray-500 ml-1">({product.profit_percentage?.toFixed(0)}%)</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge 
                                                className={product.active 
                                                    ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30 cursor-pointer' 
                                                    : 'bg-red-500/20 text-red-500 border-red-500/30 cursor-pointer'
                                                }
                                                onClick={() => handleToggleActive(product)}
                                            >
                                                {product.active ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-gray-400 hover:text-white"
                                                    onClick={() => openEditDialog(product)}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-gray-400 hover:text-red-500"
                                                    onClick={() => { setSelectedProduct(product); setDeleteDialogOpen(true); }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredCompositeProducts.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                                            <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>Nenhum produto composto cadastrado</p>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
                if (!open) {
                    setCreateDialogOpen(false);
                    setEditDialogOpen(false);
                    resetForm();
                }
            }}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white w-[95vw] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-lg sm:text-xl font-heading flex items-center gap-2">
                            <Layers className="w-5 h-5 text-rr-yellow-500" />
                            {editDialogOpen ? 'Editar Produto' : 'Novo Produto'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-2 sm:py-4 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400 text-sm">Nome *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="Ex: Dose de 10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400 text-sm">Preço de Venda *</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400 text-sm">Descrição (opcional)</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Descrição do produto..."
                            />
                        </div>

                        {/* Image Upload */}
                        <div className="space-y-2">
                            <Label className="text-gray-400 text-sm">Imagem (opcional)</Label>
                            <div className="flex gap-4">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#0a0a0a] rounded-lg border border-[#262626] flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {imagePreview || formData.image_url ? (
                                        <img 
                                            src={imagePreview || formData.image_url} 
                                            alt="Preview" 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
                                    )}
                                </div>
                                <div className="flex-1 flex flex-col justify-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={handleImageSelect}
                                        className="hidden"
                                        id="composite-product-image-upload"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full border-[#262626] text-gray-300 hover:bg-[#1a1a1a] text-sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4 mr-2" />
                                                Escolher Imagem
                                            </>
                                        )}
                                    </Button>
                                    {(imagePreview || formData.image_url) && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 text-sm"
                                            onClick={removeImage}
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Remover
                                        </Button>
                                    )}
                                    <p className="text-[10px] text-gray-500">JPG, PNG ou WebP. Máx 2MB</p>
                                </div>
                            </div>
                        </div>

                        {/* Product Search */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Adicionar Ingredientes</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    className="pl-10 bg-[#121212] border-[#262626] text-white"
                                    placeholder="Buscar produto para adicionar como ingrediente..."
                                />
                            </div>
                            {productSearch && (
                                <ScrollArea className="h-40 border border-[#262626] rounded-md">
                                    <div className="p-2 space-y-1">
                                        {filteredProducts.slice(0, 10).map(product => (
                                            <div
                                                key={product.id}
                                                className="flex items-center justify-between p-2 hover:bg-[#1a1a1a] rounded cursor-pointer"
                                                onClick={() => addIngredient(product)}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div>
                                                        <p className="text-white text-sm">{product.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            Custo: {formatCurrency(product.cost_price)}
                                                            {product.sold_by_dose && (
                                                                <span className="ml-2 text-blue-400">
                                                                    <Droplet className="w-3 h-3 inline mr-1" />
                                                                    Vendido por dose
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Plus className="w-4 h-4 text-rr-yellow-500" />
                                            </div>
                                        ))}
                                        {filteredProducts.length === 0 && (
                                            <p className="text-center text-gray-500 py-4">Nenhum produto encontrado</p>
                                        )}
                                    </div>
                                </ScrollArea>
                            )}
                        </div>

                        {/* Selected Ingredients */}
                        <div className="space-y-2">
                            <Label className="text-gray-400 text-sm">Ingredientes ({formData.ingredients.length})</Label>
                            <div className="border border-[#262626] rounded-md p-2 space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
                                {formData.ingredients.map((ing, idx) => (
                                    <div key={idx} className="p-2 sm:p-3 bg-[#121212] rounded">
                                        {/* Top row: Name and actions */}
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <Package className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-white text-sm truncate">{ing.product_name}</p>
                                                    <p className="text-[10px] sm:text-xs text-gray-500">
                                                        Custo: {formatCurrency(
                                                            ing.use_dose && ing.sold_by_dose
                                                                ? (ing.cost_price / (ing.doses_per_bottle || 1)) * ing.quantity
                                                                : ing.cost_price * ing.quantity
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-red-500 flex-shrink-0"
                                                onClick={() => removeIngredient(idx)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        
                                        {/* Bottom row: Dose toggle and quantity */}
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#262626]">
                                            {/* Dose Toggle (only show if product is sold_by_dose) */}
                                            {ing.sold_by_dose ? (
                                                <div className="flex items-center gap-1.5 px-2 py-1 bg-[#1a1a1a] rounded-md">
                                                    <Wine className={`w-3 h-3 ${!ing.use_dose ? 'text-amber-500' : 'text-gray-500'}`} />
                                                    <Switch
                                                        checked={ing.use_dose}
                                                        onCheckedChange={() => toggleUseDose(idx)}
                                                        className="data-[state=checked]:bg-blue-500 scale-90"
                                                    />
                                                    <Droplet className={`w-3 h-3 ${ing.use_dose ? 'text-blue-500' : 'text-gray-500'}`} />
                                                    <span className="text-[10px] text-gray-400 ml-1 hidden sm:inline">
                                                        {ing.use_dose ? 'Dose' : 'Garrafa'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="text-[10px] text-gray-500">Item inteiro</div>
                                            )}
                                            
                                            {/* Quantity controls */}
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    onClick={() => updateIngredientQuantity(idx, -1)}
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </Button>
                                                <span className="w-6 text-center text-white text-sm">{ing.quantity}</span>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    onClick={() => updateIngredientQuantity(idx, 1)}
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {formData.ingredients.length === 0 && (
                                    <p className="text-center text-gray-500 py-4 text-sm">Nenhum ingrediente adicionado</p>
                                )}
                            </div>
                        </div>

                        {/* Legend */}
                        {formData.ingredients.some(ing => ing.sold_by_dose) && (
                            <div className="p-2 sm:p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                                <p className="text-xs sm:text-sm text-blue-400 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                    <span>Ingredientes com dose:</span>
                                </p>
                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-[10px] sm:text-xs text-gray-400">
                                    <div className="flex items-center gap-1">
                                        <Wine className="w-3 h-3 text-amber-500" />
                                        <span><strong>Garrafa:</strong> Desconta garrafas</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Droplet className="w-3 h-3 text-blue-500" />
                                        <span><strong>Dose:</strong> Desconta doses</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ============== GRUPOS DE INGREDIENTES (OPÇÕES DO CLIENTE) ============== */}
                        <div className="space-y-3 pt-4 border-t border-rr-yellow-500/30">
                            <div className="flex items-center justify-between">
                                <Label className="text-rr-yellow-500 font-medium flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" />
                                    Grupos de Opções (Cliente Escolhe)
                                </Label>
                                <span className="text-xs text-gray-500">{formData.ingredient_groups.length} grupo(s)</span>
                            </div>
                            
                            <p className="text-xs text-gray-400">
                                Crie grupos onde o cliente escolhe <strong>uma opção</strong> de cada. Ex: Sabor do GIN, Sabor do Energético, Tipo de Gelo.
                            </p>
                            
                            {/* Criar novo grupo */}
                            <div className="flex flex-col sm:flex-row gap-2 p-3 bg-[#0a0a0a] rounded-lg border border-[#262626]">
                                <Input
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    className="bg-[#121212] border-[#262626] text-white flex-1"
                                    placeholder="Nome do grupo (Ex: Sabor GIN)"
                                />
                                <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
                                        <Switch
                                            checked={newGroupUseDose}
                                            onCheckedChange={setNewGroupUseDose}
                                            className="data-[state=checked]:bg-blue-500 scale-75"
                                        />
                                        <span className={newGroupUseDose ? 'text-blue-400' : 'text-gray-500'}>
                                            {newGroupUseDose ? 'Dose' : 'Item'}
                                        </span>
                                    </label>
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400 whitespace-nowrap"
                                        onClick={addIngredientGroup}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Criar Grupo
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Lista de grupos criados */}
                            {formData.ingredient_groups.length > 0 && (
                                <div className="space-y-3">
                                    {formData.ingredient_groups.map((group, groupIndex) => (
                                        <div 
                                            key={groupIndex} 
                                            className={`p-3 rounded-lg border-2 transition-all ${
                                                selectedGroupIndex === groupIndex 
                                                    ? 'border-rr-yellow-500 bg-rr-yellow-500/5' 
                                                    : 'border-[#262626] bg-[#0a0a0a]'
                                            }`}
                                        >
                                            {/* Header do grupo */}
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">
                                                        {group.group_name.toLowerCase().includes('gin') || group.group_name.toLowerCase().includes('destilado') ? '🥃' :
                                                         group.group_name.toLowerCase().includes('baly') || group.group_name.toLowerCase().includes('energetic') ? '⚡' :
                                                         group.group_name.toLowerCase().includes('gelo') ? '🧊' : '📦'}
                                                    </span>
                                                    <div>
                                                        <span className="text-white font-medium">{group.group_name}</span>
                                                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                                            group.use_dose 
                                                                ? 'bg-blue-500/20 text-blue-400' 
                                                                : 'bg-amber-500/20 text-amber-400'
                                                        }`}>
                                                            {group.use_dose ? 'DOSE' : 'ITEM'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="ghost"
                                                        className={`text-xs ${selectedGroupIndex === groupIndex ? 'text-rr-yellow-500' : 'text-gray-400'}`}
                                                        onClick={() => {
                                                            setSelectedGroupIndex(selectedGroupIndex === groupIndex ? null : groupIndex);
                                                            setGroupProductSearch(''); // Limpar busca ao mudar de grupo
                                                        }}
                                                    >
                                                        {selectedGroupIndex === groupIndex ? 'Fechar' : '+ Opções'}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-7 w-7 text-red-500"
                                                        onClick={() => removeIngredientGroup(groupIndex)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Opções do grupo */}
                                            <div className="flex flex-wrap gap-1.5">
                                                {group.options.length === 0 ? (
                                                    <span className="text-xs text-red-400 italic">Nenhuma opção adicionada</span>
                                                ) : (
                                                    group.options.map((option, optIndex) => (
                                                        <div 
                                                            key={optIndex}
                                                            className="flex items-center gap-1 px-2 py-1 bg-[#1a1a1a] rounded text-xs"
                                                        >
                                                            <span className="text-white">{option.product_name}</span>
                                                            <button
                                                                type="button"
                                                                className="text-red-500 hover:text-red-400 ml-1"
                                                                onClick={() => removeOptionFromGroup(groupIndex, optIndex)}
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            
                                            {/* Adicionar opções (quando selecionado) */}
                                            {selectedGroupIndex === groupIndex && (
                                                <div className="mt-3 pt-3 border-t border-[#262626]">
                                                    <div className="relative mb-2">
                                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                                        <Input
                                                            value={groupProductSearch}
                                                            onChange={(e) => setGroupProductSearch(e.target.value)}
                                                            className="pl-10 bg-[#121212] border-[#262626] text-white text-sm h-9"
                                                            placeholder={group.use_dose ? "Buscar produtos vendidos por dose..." : "Buscar produtos..."}
                                                        />
                                                    </div>
                                                    <ScrollArea className="h-32 border border-[#262626] rounded-md">
                                                        <div className="p-2 space-y-1">
                                                            {getFilteredProductsForGroup(group).slice(0, 15).map(product => (
                                                                <div
                                                                    key={product.id}
                                                                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                                                                        group.options.some(o => o.product_id === product.id)
                                                                            ? 'bg-rr-yellow-500/20 text-rr-yellow-500'
                                                                            : 'hover:bg-[#1a1a1a] text-white'
                                                                    }`}
                                                                    onClick={() => addOptionToGroup(groupIndex, product)}
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-sm">{product.name}</span>
                                                                        {product.sold_by_dose && (
                                                                            <Droplet className="w-3 h-3 text-blue-400" />
                                                                        )}
                                                                    </div>
                                                                    {group.options.some(o => o.product_id === product.id) ? (
                                                                        <Check className="w-4 h-4 text-rr-yellow-500" />
                                                                    ) : (
                                                                        <Plus className="w-4 h-4 text-gray-500" />
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {getFilteredProductsForGroup(group).length === 0 && (
                                                                <p className="text-center text-gray-500 py-4 text-xs">
                                                                    {groupProductSearch ? 'Nenhum produto encontrado' : 'Digite para buscar produtos'}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </ScrollArea>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        {formData.ingredients.length > 0 && (
                            <div className="p-3 sm:p-4 bg-[#121212] rounded-md space-y-2">
                                <div className="flex justify-between text-xs sm:text-sm">
                                    <span className="text-gray-400">Custo Total</span>
                                    <span className="text-red-400">{formatCurrency(calculateTotalCost())}</span>
                                </div>
                                <div className="flex justify-between text-xs sm:text-sm">
                                    <span className="text-gray-400">Preço de Venda</span>
                                    <span className="text-white font-bold">{formatCurrency(parseFloat(formData.price) || 0)}</span>
                                </div>
                                <div className="flex justify-between text-xs sm:text-sm pt-2 border-t border-[#262626]">
                                    <span className="text-gray-400">Lucro Estimado</span>
                                    <span className={`font-bold ${(parseFloat(formData.price) || 0) - calculateTotalCost() > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                        {formatCurrency((parseFloat(formData.price) || 0) - calculateTotalCost())}
                                    </span>
                                </div>
                                {calculateTotalCost() > 0 && (
                                    <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-400">Margem</span>
                                        <span className="text-purple-500">
                                            {(((parseFloat(formData.price) || 0) - calculateTotalCost()) / calculateTotalCost() * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="border-t border-[#262626] pt-3 sm:pt-4 flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}
                            className="border-[#262626] text-gray-400 flex-1 sm:flex-none"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={editDialogOpen ? handleEdit : handleCreate}
                            disabled={processing}
                            className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold flex-1 sm:flex-none"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (editDialogOpen ? 'Salvar' : 'Criar')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Desativar Produto Composto
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Tem certeza que deseja desativar o produto "{selectedProduct?.name}"?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleDelete}
                            disabled={processing}
                            className="bg-red-500 hover:bg-red-400 text-white"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desativar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CompositeProductsPage;
